import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { withStyles } from '@material-ui/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Snackbar } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import LinearProgress from '@material-ui/core/LinearProgress';
import RefreshIcon from '@material-ui/icons/Refresh';
import CallIcon from '@material-ui/icons/Call';

import { getCallList, initiateAutomatedCalls } from '../../services';
import { CallManagerTabs } from '../../components/CallManager/CallManagerTabs';
import { useCallManager } from '../../components/CallManager/CallManagerContext';
import { CallManagerContextProvider } from '../../components/CallManager/CallManagerContext';
import { Settings } from '@material-ui/icons';
import { getCallPayload, startVapiCall } from '../../services/vapi.service';
import { callMonitor } from '../../services/callMonitor.service';
import { useHistory } from 'react-router-dom';

const styles = theme => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    marginBottom: 20,
    color: '#1e5180',
  },
  button: {
    margin: '10px 0',
    backgroundColor: '#3a87ad',
    color: 'white',
    '&:hover': {
      backgroundColor: '#2a6d8d',
    },
  },
  filterContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: 16,
  },
  formControl: {
    minWidth: 150,
  },
  callCapacityContainer: {
    padding: 16,
    backgroundColor: '#f8f9fa',
    borderRadius: 4,
    border: '1px solid #ddd',
    marginBottom: 16,
  },
  callCapacityBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: '#e9ecef',
    marginTop: 8,
  },
  callCapacityProgress: {
    backgroundColor: '#3a87ad',
    borderRadius: 4,
  },
  actionsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '16px',
  },
  gridOuterContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    width: '100%',
    height: 'calc(100vh - 300px)',
    minHeight: '500px',
  },
  gridContainer: {
    height: '100%',
    width: '100%',
    flex: 1,
    '& .ag-root': {
      border: 'none',
    },
  },
  tabContainer: {
    display: 'flex',
    gap: '8px',
    marginRight: '16px',
  },
  tabButton: {
    backgroundColor: '#3a87ad',
    color: 'white',
    '&:hover': {
      backgroundColor: '#2a6d8d',
    },
  },
  actionButton: {
    marginRight: 8,
    textTransform: 'none',
  },
  callButton: {
    backgroundColor: '#3a87ad',
    color: 'white',
    '&:hover': {
      backgroundColor: '#2a6d8d',
    },
  },
  monitorButton: {
    backgroundColor: '#3a87ad',
    color: 'white',
    '&:hover': {
      backgroundColor: '#2a6d8d',
    },
  },
  endButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    '&:hover': {
      backgroundColor: '#bd2130',
    },
  },
  flagButton: {
    backgroundColor: '#F0AD4E',
    color: 'white',
    '&:hover': {
      backgroundColor: '#eda642',
    },
  },
});

const DashboardContent = withStyles(styles)(({ classes }) => {
  const { startCall, activeCalls, updateCallStatus } = useCallManager();
  const [callList, setCallList] = useState([]);
  const [initiatingAutomatedCalls, setInitiatingAutomatedCalls] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const history = useHistory();

  useEffect(() => {
    const initializeCallMonitor = async () => {
      try {
        await callMonitor.start();

        // Set up call update handler
        const unsubscribe = callMonitor.onUpdate((type, update) => {
          // Normalize orderLineId to string for consistent comparison (API returns string, SignalR may return number)
          const updateOrderLineId = String(update.orderLineId);

          if (type === 'initiated') {
            // New call initiated
            const queueItem = {
              orderLineId: updateOrderLineId, // Store as string for consistent matching
              customer: update.insuredName,
              phone: update.insuredPhone,
              phoneLine: 'Assistant #1',
              vapiCallId: update.callId,
              providerCallId: update.providerCallId,
              status: update.status,
              callStatus: update.callStatus,
            };
            startCall(queueItem);

            // Also update the local callList state with status for grid display
            setCallList(prevList =>
              prevList.map(item => {
                if (String(item?.assistantOverrides?.variableValues?.orderLineId) === updateOrderLineId) {
                  return {
                    ...item,
                    isActive: update.status, // Persist live status so grid reflects SignalR updates
                    vapiCallId: update.callId,
                    providerCallId: update.providerCallId,
                  };
                }
                return item;
              }),
            );
          } else if (type === 'update') {
            // Call status update
            updateCallStatus(updateOrderLineId, {
              status: update.status,
              callStatus: update.callStatus,
              duration: update.callDurationSeconds,
              transcript: update.fullTranscript,
              recordingUrl: update.recordingUrl,
              providerCallId: update.providerCallId || update.callId,
            });

            // Also update the local callList state with status for grid display
            setCallList(prevList =>
              prevList.map(item => {
                if (String(item?.assistantOverrides?.variableValues?.orderLineId) === updateOrderLineId) {
                  return {
                    ...item,
                    isActive: update.status, // Persist live status so grid reflects SignalR updates
                    providerCallId: update.providerCallId || update.callId,
                  };
                }
                return item;
              }),
            );
          }
        });

        return () => {
          unsubscribe();
          callMonitor.stop();
        };
      } catch (error) {
        console.error('Failed to initialize call monitor:', error);
        setSnackbar({
          open: true,
          message: 'Failed to connect to real-time updates',
          severity: 'error',
        });
      }
    };

    initializeCallMonitor();
    fetchCallList({ page, pageSize });

    return () => {
      callMonitor.stop();
    };
  }, []);

  const CallButton = ({ row }) => {
    // Check if the live status (passed as isActive) is 'Ready to Call' (case-insensitive)
    const isReady = row?.isActive?.toLowerCase() === 'ready to call';

    // Only render the button if the status is ready
    if (!isReady) {
      return null; // Render nothing if not ready
    }

    return (
      <Button
        variant="contained"
        size="small"
        className={`${classes.actionButton} ${classes.callButton}`}
        onClick={async () => {
          const payload = await getCallPayload(row?.orderLineId);
          const vapiCall = await startVapiCall(payload);

          const queueItem = {
            orderLineId: row?.orderLineId,
            customer: row?.insuredName,
            phone: row?.insuredPhone,
            phoneLine: 'Assistant #1',
            vapiCallId: vapiCall.id,
            providerCallId: vapiCall.id,
          };

          startCall(queueItem);
          await callMonitor.subscribeToCall(vapiCall.id);

          // Set initial status on manual call start; SignalR updates will override
          const newData = callList.map(item => {
            if (String(item?.assistantOverrides?.variableValues?.orderLineId) === String(queueItem.orderLineId)) {
              return {
                ...item,
                isActive: 'Queued', // Initial status until SignalR updates arrive
                vapiCallId: vapiCall.id,
                providerCallId: vapiCall.id,
              };
            }
            return item;
          });
          setCallList(newData);
        }}
      >
        <CallIcon fontSize="small" />
      </Button>
    );
  };

  const FlagButton = ({ row }) => (
    <Button
      variant="contained"
      size="small"
      className={`${classes.actionButton} ${classes.flagButton}`}
      onClick={() => console.log('Flag:', row)}
    >
      FLAG
    </Button>
  );

  const ViewButton = ({ row }) => (
    <Button
      variant="contained"
      size="small"
      className={`${classes.actionButton} ${classes.viewButton}`}
      onClick={() => history.push(`/call-manager/analysis/${row.providerCallId}`)}
    >
      VIEW
    </Button>
  );

  const columns = [
    {
      id: 'orderLineId',
      label: 'Order Ticket',
      valueGetter: row => row?.assistantOverrides?.variableValues?.orderLineId,
    },
    {
      id: 'insuredPhone',
      label: 'Phone Number',
      valueGetter: row => row?.assistantOverrides?.variableValues?.insuredPhone,
    },
    {
      id: 'insuredName',
      label: 'Insured Name',
      valueGetter: row => row?.assistantOverrides?.variableValues?.insuredName,
    },
    {
      id: 'customerName',
      label: 'Client',
      valueGetter: row => row?.assistantOverrides?.variableValues?.customerName,
    },
    { id: 'stateName', label: 'State', valueGetter: row => row?.assistantOverrides?.variableValues?.stateName },
    {
      id: 'insured_address',
      label: 'Address',
      valueGetter: row => row?.assistantOverrides?.variableValues?.insured_address,
    },
    { id: 'policy', label: 'Policy', valueGetter: row => row?.assistantOverrides?.variableValues?.policy },
    { id: 'agent_name', label: 'Agent', valueGetter: row => row?.assistantOverrides?.variableValues?.agent_name },
    { id: 'orderDate', label: 'Order Date', valueGetter: row => row?.assistantOverrides?.variableValues?.orderDate },
    { id: 'isActive', label: 'Status', type: 'status', valueGetter: row => row?.isActive },
    { id: 'duration', label: 'Duration', valueGetter: row => row.duration || '' },
  ];

  const fetchCallList = async queryParams => {
    setLoading(true);
    try {
      const newQueryParams = {
        ...queryParams,
        page: queryParams.page + 1,
        sortByMostRecentDate: true,
      };
      const data = await getCallList(false, newQueryParams);

      const filteredList = data.results.filter(r => r.assistantOverrides.variableValues.callStatus !== 'FR-ASSIGNED') || [];

      setCallList(filteredList);
      setTotalCount(data.totalCount);

      setLoading(false);
      setSnackbar({
        open: true,
        message: `Retrieved ${data.results?.length || 0} calls to process`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error fetching call list:', error);
      setLoading(false);
      setSnackbar({
        open: true,
        message: 'Failed to retrieve call list',
        severity: 'error',
      });
    }
  };

  const initiateAutomatedCalling = async () => {
    try {
      let result = await initiateAutomatedCalls();
      if (result) {
        setSnackbar({
          open: true,
          message: 'Automated calls initiated successfully!',
          severity: 'success',
        });      
      } else {
        setSnackbar({
          open: true,
          message: 'Could Not Initiate Automated Calls',
          severity: 'error',
        });
      }
    } catch (err) {
      console.log('Could not initiate automated calls' + err);

      setSnackbar({
        open: true,
        message: 'Could Not Initiate Automated Calls',
        severity: 'error',
      });
    }
  };

  // Get call capacity information
  const maxCapacity = 10;

  // Filter activeCalls to count only those that are genuinely in an active state
  const trulyInProgressCalls = activeCalls.filter(call => {
    const status = call.status ? call.status.toLowerCase() : '';
    // Define statuses that mean a call is NOT active anymore
    const nonActiveStatuses = ['disconnected', 'ended', 'completed'];
    return !nonActiveStatuses.includes(status);
  });

  const currentActiveCalls = trulyInProgressCalls.length;
  const callCapacityPercentage = maxCapacity > 0 ? (currentActiveCalls / maxCapacity) * 100 : 0;

  const handleAutomatedCallSimulation = async () => {
    setInitiatingAutomatedCalls(true);
    let res = await initiateAutomatedCalling();
    setInitiatingAutomatedCalls(false);
  }
  

  return (
    <div className={cn('flex-1', 'col-start-start', classes.root)}>
      <div
        style={{ width: '100%', justifyContent: 'space-between', justifyItems: 'center' }}
        className={cn('flex flex-row')}
      >
        <h1 className={cn('text-left', classes.title)}>Call Queue Manager</h1>
        <Button
          variant="contained"
          style={{ margin: '20px' }}
          className={classes.button}
          onClick={() => (window.location.href = '/#/call-manager/settings')}
        >
          <Settings />
        </Button>
      </div>

      {/* Call Capacity Indicator */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <div className={classes.callCapacityContainer}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">Call Capacity:</Typography>
            <Typography variant="body2" style={{ color: '#3a87ad' }}>
              {currentActiveCalls}/{maxCapacity} active calls
            </Typography>
          </div>
          <div className={classes.callCapacityBar}>
            <LinearProgress
              variant="determinate"
              value={callCapacityPercentage}
              classes={{ bar: classes.callCapacityProgress }}
            />
          </div>
        </div>
        <Button variant="contained" className={classes.button} onClick={() => handleAutomatedCallSimulation()}>
          {initiatingAutomatedCalls ? 'Initiating Calls...' : 'Initiate 10 Automated Calls'}
        </Button>
      </div>

      <div className={classes.gridOuterContainer}>
        <CallManagerTabs
          columns={columns}
          loading={loading}
          data={callList.map(item => {
            const orderLineId = String(item?.assistantOverrides?.variableValues?.orderLineId);
            // Normalize to string for matching (activeCalls stores string orderLineId from SignalR handler)
            const activeCall = activeCalls.find(call => String(call.orderLineId) === orderLineId);
            const itemCallStatus = item?.assistantOverrides?.variableValues?.callStatus;

            // Prioritize: activeCall context > item.isActive (live SignalR status) > API callStatus > fallback
            const displayStatus = activeCall?.status || item.isActive || itemCallStatus || 'Call Hold';

            // Check if the status allows showing duration
            const lowerDisplayStatus = displayStatus?.toLowerCase();
            const shouldShowDuration = lowerDisplayStatus === 'queued' || lowerDisplayStatus === 'in-progress';
            const durationValue = activeCall?.duration || '';

            return {
              ...item.assistantOverrides.variableValues,
              isActive: displayStatus,
              duration: shouldShowDuration ? durationValue : '',
              providerCallId: activeCall?.providerCallId || item.providerCallId || item.assistantOverrides.variableValues.providerCallId || '',
            };
          })}
          actionButtons={[
            // {
            //   name: 'CallButton',
            //   component: CallButton,
            // },
            {
              name: 'ViewButton',
              component: ViewButton,
            },
          ]}
          title="Call Queue"
          pageSize={pageSize}
          totalCount={totalCount}
          page={page}
          setPage={setPage}
          setPageSize={setPageSize}
          fetchCallList={fetchCallList}
        />
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
});

const CallManagerDashboard = props => (
  <CallManagerContextProvider>
    <DashboardContent {...props} />
  </CallManagerContextProvider>
);

CallManagerDashboard.propTypes = {};

CallManagerDashboard.defaultProps = {};

export default withStyles(styles)(CallManagerDashboard);
